import type { ClassValue } from '@vs-shop/utils';
import Image from '@vs-shop/image-link/Image';

import type { VsImageProps } from '@vs-shop/headless/useVsImage';

/**
 * The frame-and-fill shape every section's media part renders: a box the
 * section sizes, with one crop inside it filling that box.
 *
 * Shared because the awkward part of it is the same everywhere. A media part
 * renders TWO elements, so it cannot take `ComponentProps<'div'>` and spread —
 * `style` would land on whichever of the two the implementation happened to
 * pick. Each section instead declares `className` alone and lands it on the
 * frame, so its picture and its no-picture stand-in stay interchangeable in the
 * same box.
 *
 * What is NOT shared is the pair of recipes: how big the frame is and whether
 * the crop is contained or cropped are the section's own answers, and they
 * genuinely differ — a subcategory's mark is a subject and is contained, an
 * article's thumbnail is a scene and fills.
 *
 * Not exported from the package. It is anatomy below the line a tenant writes:
 * every caller is a part in this package, and what a tenant composes is the
 * part.
 */

/**
 * What a section's media part accepts: the portable single-image contract,
 * minus the two keys the frame owns.
 *
 * `src` is final and renderable — CDN/Liip/`{filter}` resolution stays app-side
 * (spec § "Media parts") — and there is no `sources`, because a section framing
 * one crop is not art-directed and gains no `<picture>`.
 */
export type FramedMediaProps = Omit<VsImageProps, 'className' | 'fill'> & {
  readonly className?: ClassValue;
};

/**
 * `className` is absent rather than ignored: the part resolved it into
 * `frameClassName` already, so accepting it again would be a second way to
 * paint the same element.
 */
export type FramedMediaRenderProps = Omit<FramedMediaProps, 'className'> & {
  /** The slot the frame is reachable by. */
  readonly dataSlot: string;
  /** The frame's recipe already merged with the caller's `className`. */
  readonly frameClassName: string;
  /** How the crop sits inside the frame — the section's recipe, never a prop. */
  readonly imageClassName: string;
};

export const FramedMedia = ({
  dataSlot,
  frameClassName,
  imageClassName,
  ...props
}: FramedMediaRenderProps) => (
  <div data-slot={dataSlot} className={frameClassName}>
    {/*
      Renders through the `Image` seam, so a host that aliases the specifier
      gets its resizing, `srcset` and lazy behaviour, and one that does not
      resolves the portable plain `<img>`.
    */}
    <Image {...props} className={imageClassName} />
  </div>
);
